/**
 * Telling apart the reasons a request did not answer, because one sentence for all of them sends people
 * to check their wifi when their session has expired (Thomas, 2026-09-25).
 *
 * The screen used to say *Autho could not reach its server. Check your connection and try again* for
 * **every** failure of `/api/me` — including a 401 that already carried the words *Sign in again*. The
 * three states below need three different actions from the person, and two of them are not about their
 * connection at all:
 *
 *  - `offline` — the browser itself says there is no network. Nothing is wrong with Autho.
 *  - `unreachable` — the request never got an answer and the browser believes it is online. The server
 *    is down, restarting, or not started. Telling somebody to check their connection here is a lie.
 *  - `expired` — the server answered, and its answer was *this session is no longer valid*. The fix is
 *    to sign in again, which the app does rather than describes.
 *
 * `refused` and `server` are kept apart from `unknown` for the same reason: each has a different next
 * step, and a shared message is how a product stops being trusted about any of them.
 */
import { ApiRequestError } from './api.ts';

export type Trouble =
  | 'offline'
  | 'unreachable'
  | 'expired'
  | 'refused'
  | 'server'
  | 'too_soon'
  | 'not_configured'
  | 'provider_not_enabled'
  | 'reconnect_needed'
  | 'provider_too_fast'
  | 'provider_unavailable'
  | 'upstream'
  | 'wrong_profile'
  | 'wall'
  | 'unmarked'
  | 'wrong_words'
  | 'stated'
  | 'unknown';

/**
 * The two codes that carry no sentence of their own. `internal` is what the server's unhandled-error
 * handler emits, `unknown` what this client invents when a response had no body to read. For everything
 * else the server chose a code deliberately and wrote a sentence to go with it.
 */
const UNSTATED = new Set(['internal', 'unknown', '']);

/**
 * Codes that have a sentence here, in both languages, rather than the server's English one.
 *
 * A server message is better than *something went wrong*, but it is not better than a sentence in the
 * reader's own language, and these four are the ones a person actually meets.
 */
const TRANSLATED: Record<string, Trouble> = {
  provider_not_enabled: 'provider_not_enabled',
  reconnect_needed: 'reconnect_needed',
  provider_too_fast: 'provider_too_fast',
  provider_unavailable: 'provider_unavailable',
  not_configured: 'not_configured',
  // These two already have a sentence here in both languages, and it is the same sentence the server
  // sends in English. Translated rather than quoted, so a Norwegian reader gets Norwegian.
  forbidden: 'refused',
  unauthenticated: 'expired',
  // Brønnøysund by name, because the register being down is a thing a Norwegian reader knows.
  upstream_unavailable: 'upstream',
  /**
   * **The wall** (R3). A screen asked for something of the other kind of profile — a personal screen in a
   * company, or the reverse — or our own code reached into another profile and was stopped. The first is the
   * person's to fix by switching profile; the other two are ours, said as ours (G50), in their language.
   */
  wrong_profile: 'wrong_profile',
  wall: 'wall',
  unmarked_route: 'unmarked',
  words_not_in_this_profile: 'wrong_words',
};

/**
 * What went wrong, from the error and from whether the browser thinks it has a network.
 *
 * `online` is passed in rather than read here so this stays a pure function a test can drive, and so the
 * caller reads it at render time through a subscription rather than once at module load.
 *
 * **An error that is not an `ApiRequestError` never reached the server**: `unwrap` builds that class from
 * a response, so anything else — a `TypeError` from `fetch`, an abort — means no answer came back.
 */
export function troubleOf(error: unknown, online: boolean): Trouble {
  if (!(error instanceof ApiRequestError)) return online ? 'unreachable' : 'offline';
  // A response arrived, so the network is not the story whatever the browser thinks of itself.
  if (error.status === 401) return 'expired';
  /**
   * **The code decides before the status does** (F208). The estimate answered 503 with *Autho has not
   * finished switching this provider on for itself … nothing for you to do*, and this function replaced it
   * with *Autho's server hit an error. It has been logged*, because 503 is ≥ 500. Every status band here
   * ends in a sentence that names no action, so any status band applied before the code throws away the
   * one thing the server took the trouble to say.
   */
  const translated = TRANSLATED[error.code];
  if (translated) return translated;
  if (!UNSTATED.has(error.code)) return 'stated';
  if (error.status === 403) return 'refused';
  if (error.status >= 500) return 'server';
  return 'unknown';
}

/**
 * Why signing in did not work, from what Supabase's own error carried (F202).
 *
 * **The one that matters is `too_soon`.** Pressing the button twice — or having the page open in two
 * tabs — asks for a second magic link inside the service's minimum interval, and it answers 429
 * `over_email_send_rate_limit`. That is an ordinary thing a person does and can act on: wait a moment,
 * and the first link that was sent still works. It reached the screen as *Something went wrong. The
 * error has been logged*, which names no action, blames the product, and is the sentence a first-time
 * user meets when the only thing wrong is that they were quick.
 *
 * `not_configured` is the opposite end: the app was served without a Supabase URL, so nobody signing in
 * can do anything about it and the screen should not pretend otherwise.
 */
export function signInTrouble(failure: { status: number; code: string }, online: boolean): Trouble {
  if (failure.code === 'not_configured') return 'not_configured';
  if (failure.status === 429 || failure.code === 'over_email_send_rate_limit') return 'too_soon';
  // Supabase's client answers 0 when the request never reached the service at all.
  if (failure.status === 0) return online ? 'unreachable' : 'offline';
  if (failure.status >= 500) return 'server';
  if (failure.status >= 400) return 'refused';
  return 'unknown';
}

/** The message key for each, so the mapping is one table rather than a conditional in every screen. */
export const TROUBLE_MESSAGE = {
  offline: 'app.error.offline',
  unreachable: 'app.error.unreachable',
  expired: 'app.error.expired',
  refused: 'app.error.refused',
  server: 'app.error.server',
  too_soon: 'app.error.tooSoon',
  not_configured: 'app.error.notConfigured',
  provider_not_enabled: 'app.error.providerNotEnabled',
  reconnect_needed: 'app.error.reconnect',
  provider_too_fast: 'app.error.providerTooFast',
  provider_unavailable: 'app.error.providerUnavailable',
  upstream: 'app.error.upstream',
  wrong_profile: 'app.error.wrongProfile',
  wall: 'app.error.wall',
  unmarked: 'app.error.unmarked',
  wrong_words: 'app.error.wrongWords',
  // The server's own sentence, shown as it was written. Not a key: a key here would be this table
  // claiming to know better than the answer it is looking at.
  stated: 'app.error.generic',
  unknown: 'app.error.generic',
} as const satisfies Record<Trouble, string>;

/** True when the sentence to show is the server's own, not one from the table above. */
export const speaksForItself = (trouble: Trouble): boolean =>
  trouble === 'stated' || trouble === 'unknown';
